// Reasoning text templates for the guest progressive fit section on the
// confidence card. Pure functions — no React, no DOM, no analytics. Output
// is a structured list of rows + based_on/limited_by lines that the card
// renders.
//
// The library is intentionally starter-sized: it covers the most common
// fit_type × body_shape combinations and falls back gracefully for anything
// not matched. Extend over time without breaking the public shape.

import type { GuestModelProfile, HeightValue } from "./profileFieldOptions";
import { HEIGHT_OPTIONS_BY_GENDER } from "./profileFieldOptions";

export type Gender = "women" | "men" | "non-binary";

export interface ReasoningContext {
  // Garment data (any may be null)
  fit_type: string | null;
  fabric_composition: string | null;
  category: string | null;
  size_in_chart: boolean;

  // User inputs
  size: string | null;
  height: GuestModelProfile["height"];
  body_shape: string | null;
  body_size: string | null;

  // Context
  gender: Gender | null;
  retailer_name: string | null;
}

export interface ReasoningRow {
  // A-7b PR 6b — 'sparkle' added for T2_GARMENT_ATTRIBUTES styling rows.
  // Renderer in PurchaseConfidenceCard switches on icon === 'sparkle'.
  icon: "check" | "warning" | "info" | "sparkle";
  text: string;
  // Optional discriminator for future row taxonomies. 'styling' marks rows
  // emitted by the styling-pattern matcher; existing rows omit this field.
  type?: "check" | "warning" | "info" | "positive" | "styling";
}

export interface ReasoningResult {
  rows: ReasoningRow[];
  based_on: string;
  limited_by: string | null;
}

export function hasFabricStretch(fabric_composition: string | null): boolean {
  if (!fabric_composition) return false;
  const lower = fabric_composition.toLowerCase();
  return (
    lower.includes("elastane") ||
    lower.includes("spandex") ||
    lower.includes("lycra") ||
    lower.includes("stretch")
  );
}

export type ConfidenceLevel = "low" | "medium" | "medium-high";

export function getConfidenceLevel(
  profile: Partial<GuestModelProfile> & {
    size?: string | null;
    gender: Gender | null;
  },
): ConfidenceLevel {
  if (!profile.size) return "low";
  if (!profile.height) return "medium";
  if (profile.gender === "men") {
    return profile.body_shape ? "medium-high" : "medium";
  }
  if (!profile.body_shape) return "medium";
  if (!profile.body_size) return "medium";
  return "medium-high";
}

// ---- helpers ------------------------------------------------------------

function fitTypeGuidance(fit: string): string {
  switch (fit) {
    case "slim":
      return "Slim cuts run narrow in shoulders and chest";
    case "regular":
      return "Regular cut works for most builds";
    case "relaxed":
      return "Relaxed cut has extra room through the body";
    case "oversized":
      return "Oversized cut intentionally roomy — consider sizing down if you prefer fitted";
    case "fitted":
      return "Fitted cut hugs the body — check measurements if between sizes";
    case "straight":
      return "Straight cut falls in a clean line from shoulders to hem";
    case "tapered":
      return "Tapered cut narrows towards the hem";
    case "loose":
      return "Loose cut sits away from the body for comfort";
    default:
      return `${capitalise(fit)} cut`;
  }
}

function capitalise(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s;
}

function heightLabel(
  height: HeightValue | null,
  gender: Gender | null,
): string | null {
  if (!height || !gender) return height ?? null;
  const opts = HEIGHT_OPTIONS_BY_GENDER[gender];
  const found = opts.find((o) => o.value === height);
  return found?.label ?? height;
}

// shape × fit combination library. Each value yields one row.
type ShapeFitRow = { icon: ReasoningRow["icon"]; text: string };
const SHAPE_FIT_LIBRARY: Record<string, ShapeFitRow> = {
  // women
  "athletic|slim": {
    icon: "warning",
    text: "Slim cut may be tight across the shoulders for athletic builds — consider sizing up",
  },
  "athletic|relaxed": {
    icon: "check",
    text: "Relaxed cut suits athletic builds well",
  },
  "athletic|fitted": {
    icon: "info",
    text: "Fitted cut works for athletic builds — check shoulder measurements",
  },
  "hourglass|fitted": {
    icon: "check",
    text: "Fitted cut emphasises waist definition — works for hourglass shapes",
  },
  "hourglass|slim": {
    icon: "check",
    text: "Slim cut highlights hourglass curves",
  },
  "hourglass|oversized": {
    icon: "info",
    text: "Oversized cut hides waist definition — pair with a belt for shape",
  },
  "pear|slim": {
    icon: "warning",
    text: "Slim cut may be tight through hips — consider sizing up for hips, or pick a relaxed cut",
  },
  "pear|relaxed": {
    icon: "check",
    text: "Relaxed cut flatters pear shapes",
  },
  "pear|fitted": {
    icon: "warning",
    text: "Fitted bottoms may pull through the hips — check stretch content",
  },
  "curvy|fitted": {
    icon: "warning",
    text: "Fitted cut may be tight — consider sizing up for comfort",
  },
  "curvy|relaxed": {
    icon: "check",
    text: "Relaxed cut suits curvy builds",
  },
  "curvy|slim": {
    icon: "warning",
    text: "Slim cut may pull at the bust or hips — consider sizing up",
  },
  "petite|oversized": {
    icon: "warning",
    text: "Oversized cut may overwhelm petite frames — consider sizing down",
  },
  "petite|slim": {
    icon: "check",
    text: "Slim cut flatters petite frames",
  },
  "petite|relaxed": {
    icon: "info",
    text: "Relaxed cut on petite frames can read as oversized — try the smaller size first",
  },
  // men + non-binary
  "slim_lean|slim": {
    icon: "check",
    text: "Slim cut suits slim/lean builds",
  },
  "slim_lean|relaxed": {
    icon: "info",
    text: "Relaxed cut on a lean build may swamp the frame",
  },
  "slim_lean|oversized": {
    icon: "info",
    text: "Oversized cut intentionally roomy on lean frames — size down for a closer fit",
  },
  "athletic_broad|slim": {
    icon: "warning",
    text: "Slim cut often pulls at the shoulders for broad/athletic builds — size up or look for stretch",
  },
  "athletic_broad|regular": {
    icon: "check",
    text: "Regular cut accommodates broad shoulders well",
  },
  "athletic_broad|relaxed": {
    icon: "check",
    text: "Relaxed cut suits athletic/broad builds",
  },
  "regular_average|regular": {
    icon: "check",
    text: "Regular cut on a regular build — true-to-size most of the time",
  },
  "regular_average|slim": {
    icon: "info",
    text: "Slim cut narrows through the chest and waist",
  },
  "stocky|slim": {
    icon: "warning",
    text: "Slim cut may be tight through the chest and midsection — consider sizing up",
  },
  "stocky|regular": {
    icon: "check",
    text: "Regular cut accommodates a stocky build",
  },
  "stocky|relaxed": {
    icon: "check",
    text: "Relaxed cut comfortable for stocky builds",
  },
  "plus_size|slim": {
    icon: "warning",
    text: "Slim cut likely too tight — choose a regular or relaxed cut",
  },
  "plus_size|relaxed": {
    icon: "check",
    text: "Relaxed cut suits plus-size builds",
  },
  "balanced_regular|regular": {
    icon: "check",
    text: "Regular cut works for balanced/regular builds",
  },
  "curvy_fuller|fitted": {
    icon: "warning",
    text: "Fitted cut may be tight — consider sizing up",
  },
  "curvy_fuller|relaxed": {
    icon: "check",
    text: "Relaxed cut suits curvy/fuller builds",
  },
};

// Safe, universally-flattering styling notes per body shape. Used as the
// fallback when no specific shape×fit row matches. Keep claims conservative.
const SHAPE_DEFAULT_NOTES: Record<string, string> = {
  hourglass: "Hourglass shape — relaxed cuts usually work for this build",
  pear: "Pear shape — A-line cuts usually flatter this build",
  athletic: "Athletic build — structured cuts usually work well",
  athletic_broad: "Athletic build — structured cuts usually work well",
  curvy: "Curvy shape — relaxed cuts usually offer comfort",
  curvy_fuller: "Curvy shape — relaxed cuts usually offer comfort",
  petite: "Petite frame — fitted cuts usually flatter this build",
  plus_size: "Plus size — relaxed cuts usually offer comfort",
  slim_lean: "Slim build — most cuts work; size down if oversized swamps you",
  stocky: "Stocky build — regular cuts usually work well",
  regular_average: "Regular build — most cuts fit true",
  balanced_regular: "Regular build — most cuts fit true",
  rectangle: "Rectangle shape — fitted cuts add definition",
};

function shapeRow(shape: string, fit: string | null): ShapeFitRow | null {
  if (!fit) return null;
  return SHAPE_FIT_LIBRARY[`${shape}|${fit}`] ?? null;
}

// ---- main ---------------------------------------------------------------

/** @deprecated Use `generateConfidenceAssessment` — combines reasoning + recommendation + brand-fit into one source of truth. */
export function generateReasoning(ctx: ReasoningContext): ReasoningResult {
  const rows: ReasoningRow[] = [];

  // Pre-input state
  if (!ctx.size) {
    rows.push({
      icon: "info",
      text: "Garment data available — add your details for personalised fit",
    });
    return {
      rows,
      based_on: "Garment information",
      limited_by: "Your size, height, and shape would help us be specific",
    };
  }

  // SIZE row
  if (ctx.size_in_chart && ctx.retailer_name) {
    rows.push({
      icon: "check",
      text: `Size ${ctx.size} appears in ${ctx.retailer_name}'s size chart`,
    });
  } else {
    rows.push({ icon: "check", text: `Size ${ctx.size} noted` });
  }

  // fit_type guidance
  if (ctx.fit_type) {
    rows.push({ icon: "info", text: fitTypeGuidance(ctx.fit_type) });
  }

  // stretch hint
  if (hasFabricStretch(ctx.fabric_composition)) {
    rows.push({
      icon: "info",
      text: "Fabric contains stretch — more forgiving on fit",
    });
  }

  if (!ctx.height) {
    return {
      rows,
      based_on: "Your size + garment details",
      limited_by: "Your height and body shape would help refine this",
    };
  }

  // HEIGHT row
  const hLabel = heightLabel(ctx.height, ctx.gender);
  const cat = (ctx.category ?? "").toLowerCase();
  if (
    (ctx.height === "tall" || ctx.height === "short") &&
    (cat === "top" || cat === "outerwear")
  ) {
    const direction = ctx.height === "tall" ? "short" : "long";
    rows.push({
      icon: "warning",
      text: `This may run ${direction} for your height — check length measurements`,
    });
  } else if (cat === "dress" && ctx.height === "tall") {
    rows.push({
      icon: "warning",
      text: "Dress lengths vary — check the size guide for your height",
    });
  } else if (
    ctx.height === "average_short" ||
    ctx.height === "average_tall"
  ) {
    rows.push({
      icon: "check",
      text: `Height ${hLabel} works well with this style`,
    });
  } else {
    rows.push({ icon: "check", text: `Height ${hLabel} noted` });
  }

  if (!ctx.body_shape) {
    return {
      rows,
      based_on: "Your size + height + garment details",
      limited_by: "Your body shape would help us be more specific",
    };
  }

  // BODY SHAPE × FIT row
  const sRow = shapeRow(ctx.body_shape, ctx.fit_type);
  if (sRow) {
    rows.push(sRow);
  } else {
    const shapeKey = ctx.body_shape.toLowerCase();
    const note = SHAPE_DEFAULT_NOTES[shapeKey];
    if (note) {
      rows.push({ icon: "check", text: note });
    } else {
      const shapeLabel = ctx.body_shape.replace(/_/g, " ");
      rows.push({
        icon: "check",
        text: `${capitalise(shapeLabel)} shape noted`,
      });
    }
  }

  // Men finish here (body_build covers shape+size)
  if (ctx.gender === "men") {
    return {
      rows,
      based_on: "Your size + height + build + garment details",
      limited_by: null,
    };
  }

  if (!ctx.body_size) {
    return {
      rows,
      based_on: "Your size + height + shape + garment details",
      limited_by: "Your build would refine this further",
    };
  }

  // BODY SIZE row (women / non-binary)
  if (
    (ctx.body_size === "fuller" || ctx.body_size === "plus_size") &&
    ctx.fit_type === "slim"
  ) {
    rows.push({
      icon: "warning",
      text: "Consider sizing up for comfort with this slim cut",
    });
  } else if (ctx.body_size === "slim" && ctx.fit_type === "oversized") {
    rows.push({
      icon: "info",
      text: "Oversized cuts intentionally roomy on slimmer builds",
    });
  } else {
    const sizeLabel = ctx.body_size.replace(/_/g, " ");
    rows.push({ icon: "check", text: `${capitalise(sizeLabel)} build noted` });
  }

  return {
    rows,
    based_on: "Your full profile + garment details",
    limited_by: null,
  };
}

// ===========================================================================
// Recommendation engine (Build 1)
// ---------------------------------------------------------------------------
// Pure function. Given garment data + guest/user profile, returns a sized
// recommendation with a confidence level. No UI, no side effects. Consumed by
// PurchaseConfidenceCard in Build 2.
// ===========================================================================

export interface RecommendationContext {
  // Garment data
  fit_type: string | null;
  fabric_composition: string | null;
  category: string | null;
  size_chart: Record<string, unknown> | null;
  fit_copy: string | null;

  // User data
  user_size: string | null;
  user_height: string | null;
  user_body_shape: string | null;
  user_body_size: string | null;
  user_gender: Gender | null;
}

export interface RecommendationResult {
  recommended_size: string | null;
  recommendation_type: "absolute" | "adjustment" | "hedged" | "prompt_user";
  recommendation_text: string;
  reasoning: string;
  confidence_level: "low" | "medium" | "medium-high" | "high";
  needs_user_data: boolean;
}

const SIZE_ORDER = ["XS", "S", "M", "L", "XL", "2XL", "3XL"] as const;

function adjustSize(size: string, direction: "up" | "down"): string {
  const idx = SIZE_ORDER.indexOf(size as (typeof SIZE_ORDER)[number]);
  if (idx === -1) return size; // unknown size token — return as-is
  if (direction === "up") return SIZE_ORDER[Math.min(idx + 1, SIZE_ORDER.length - 1)];
  return SIZE_ORDER[Math.max(idx - 1, 0)];
}

type Adjustment = "up" | "down" | "none";

function parseFitCopy(fit_copy: string | null): Adjustment {
  if (!fit_copy) return "none";
  const lower = fit_copy.toLowerCase();
  if (/(runs small|size up|fits small)/.test(lower)) return "up";
  if (/(runs large|size down|fits large|runs big)/.test(lower)) return "down";
  return "none";
}

function fitCopyLocksSize(fit_copy: string | null): boolean {
  if (!fit_copy) return false;
  const lower = fit_copy.toLowerCase();
  return /(fits true to size|true to size|take your normal size|fits as expected)/.test(
    lower,
  );
}

function bumpConfidence(
  level: RecommendationResult["confidence_level"],
): RecommendationResult["confidence_level"] {
  switch (level) {
    case "low":
      return "medium";
    case "medium":
      return "medium-high";
    case "medium-high":
      return "high";
    default:
      return "high";
  }
}

const SIZE_UP_SHAPES = new Set([
  "athletic",
  "curvy",
  "plus_size",
  "curvy_fuller",
  "athletic_broad",
  "stocky",
]);

const SIZE_DOWN_SHAPES = new Set(["petite", "slim_lean", "slim"]);

// Bare/generic clothing tokens that default to "regular" fit when no other
// indicator is present. Enables the shape rule to engage with a baseline
// assumption when garment_data is missing (blocked retailers, never-scraped
// URLs). False positives (treating a slim garment as regular) are safer than
// hedging when the profile has a strong shape signal (e.g. plus_size body).
const REGULAR_DEFAULT_TOKENS = [
  "top", "shirt", "blouse", "t-shirt", "tee", "tank",
  "dress", "skirt",
  "trousers", "pants", "jeans", "shorts", "leggings",
  "jacket", "coat", "blazer", "cardigan", "jumper", "sweater", "hoodie",
];

/**
 * Infers fit_type from category string when explicit fit_type is missing.
 * Precedence: explicit slim/fitted/oversized indicators win; otherwise bare
 * clothing tokens default to "regular" so the shape rule can engage.
 */
export function inferFitTypeFromCategory(
  category: string | null | undefined,
): "slim" | "regular" | "oversized" | null {
  if (!category) return null;
  const normalised = category.toLowerCase().trim();

  const slimIndicators = ["skinny", "slim", "fitted", "bodycon", "tapered"];
  const oversizedIndicators = ["oversized", "slouchy"];

  if (slimIndicators.some((ind) => normalised.includes(ind))) return "slim";
  if (oversizedIndicators.some((ind) => normalised.includes(ind))) return "oversized";
  if (REGULAR_DEFAULT_TOKENS.some((token) => normalised.includes(token))) {
    return "regular";
  }
  return null;
}

export function shapeAdjustmentForFit(
  fit_type: string | null,
  body_shape: string | null,
  body_size: string | null,
  category: string | null = null,
): Adjustment {
  const effectiveFitType = fit_type ?? inferFitTypeFromCategory(category);
  if (!fit_type && effectiveFitType) {
    console.log(
      `[shape-rule] fit_type inferred from category: category="${category}" → fit_type="${effectiveFitType}"`,
    );
  }
  if (!effectiveFitType) return "none";
  const shape = body_shape ?? "";
  const size = body_size ?? "";
  if (effectiveFitType === "slim" || effectiveFitType === "fitted") {
    if (SIZE_UP_SHAPES.has(shape) || SIZE_UP_SHAPES.has(size)) return "up";
  }
  if (effectiveFitType === "oversized") {
    if (SIZE_DOWN_SHAPES.has(shape) || SIZE_DOWN_SHAPES.has(size)) return "down";
  }
  if (effectiveFitType === "regular") {
    // Conservative: only fire for plus-body profiles where a standard cut is
    // likely to be tight. Other shapes get "none" — no over-firing.
    if (size === "plus_size" || shape === "plus_size") return "up";
  }
  return "none";
}

function reasoningForAdjustment(
  adjustment: Adjustment,
  source: "fit_copy" | "shape",
  fit_type: string | null,
  body_shape: string | null,
): string {
  if (source === "fit_copy") {
    return adjustment === "up"
      ? "Runs small — sizing up gives more room"
      : "Runs large — sizing down for a closer fit";
  }
  // shape-derived
  if (adjustment === "up") {
    if (body_shape === "athletic" || body_shape === "athletic_broad") {
      return `${fit_type === "fitted" ? "Fitted" : "Slim"} cut may be tight across shoulders for athletic builds`;
    }
    if (body_shape === "curvy" || body_shape === "curvy_fuller") {
      return `${fit_type === "fitted" ? "Fitted" : "Slim"} cut may pull at curves — sizing up gives more comfort`;
    }
    if (body_shape === "plus_size" || body_shape === "stocky") {
      return `${fit_type === "fitted" ? "Fitted" : "Slim"} cut runs narrow — sizing up for comfort`;
    }
    return `${fit_type ?? "Slim"} cut runs narrow — sizing up gives more room`;
  }
  // down
  return "Oversized cut runs roomy on slimmer frames — sizing down for a closer fit";
}

function reasoningForAbsolute(fit_type: string | null): string {
  if (!fit_type) return "True to size based on your usual size";
  switch (fit_type) {
    case "regular":
      return "Regular cut fits true to size";
    case "relaxed":
      return "Relaxed cut fits true to size with extra room";
    case "slim":
    case "fitted":
      return "Slim cut — confirm in the size guide";
    case "oversized":
      return "Oversized cut intentionally roomy — true to size for that look";
    default:
      return `${fit_type} cut — true to size based on your usual size`;
  }
}

/** @deprecated Use `generateConfidenceAssessment` — unified async function that incorporates brand-fit signals. */
export function generateRecommendation(
  ctx: RecommendationContext,
): RecommendationResult {
  // Step 1 — no size from user
  if (!ctx.user_size) {
    return {
      recommended_size: null,
      recommendation_type: "prompt_user",
      recommendation_text: "Tell us your usual size to get a recommendation",
      reasoning: "We need your usual size to recommend the right fit",
      confidence_level: "low",
      needs_user_data: true,
    };
  }

  // Step 2 — user has size, garment has no useful data
  if (!ctx.fit_type && !ctx.fit_copy && !ctx.size_chart) {
    return {
      recommended_size: ctx.user_size,
      recommendation_type: "hedged",
      recommendation_text: `Buy your usual size ${ctx.user_size}`,
      reasoning: "Limited fit data — check the size guide before buying",
      confidence_level: "low",
      needs_user_data: false,
    };
  }

  // Step 3 — fit_copy "true to size" locks the recommendation early.
  const fit_copy_locks_size = fitCopyLocksSize(ctx.fit_copy);

  // Step 3 — determine adjustment (skipped when fit_copy locks size).
  let adjustment: Adjustment = fit_copy_locks_size
    ? "none"
    : parseFitCopy(ctx.fit_copy);
  let source: "fit_copy" | "shape" = "fit_copy";
  if (!fit_copy_locks_size && adjustment === "none") {
    const shapeAdj = shapeAdjustmentForFit(
      ctx.fit_type,
      ctx.user_body_shape,
      ctx.user_body_size,
      ctx.category,
    );
    if (shapeAdj !== "none") {
      adjustment = shapeAdj;
      source = "shape";
    }
  }

  // Step 4 — apply adjustment
  const recommended_size =
    adjustment === "none" ? ctx.user_size : adjustSize(ctx.user_size, adjustment);

  // Step 5 — confidence
  const hasFitCopy = !!ctx.fit_copy;
  const hasSizeChart = !!ctx.size_chart;
  const hasShape = !!ctx.user_body_shape || !!ctx.user_body_size;
  let confidence_level: RecommendationResult["confidence_level"] = "low";
  if (ctx.fit_type && hasFitCopy && hasSizeChart && hasShape) {
    confidence_level = "medium-high";
  } else if (ctx.fit_type && (hasFitCopy || hasSizeChart)) {
    confidence_level = "medium";
  } else {
    confidence_level = "low";
  }
  if (fit_copy_locks_size) {
    confidence_level = bumpConfidence(confidence_level);
  }

  if (fit_copy_locks_size) {
    return {
      recommended_size,
      recommendation_type: "absolute",
      recommendation_text: `Buy size ${recommended_size}`,
      reasoning: "Fits true to size — your usual size works",
      confidence_level,
      needs_user_data: false,
    };
  }

  if (adjustment === "none") {
    return {
      recommended_size,
      recommendation_type: "absolute",
      recommendation_text: `Buy size ${recommended_size}`,
      reasoning: reasoningForAbsolute(ctx.fit_type),
      confidence_level,
      needs_user_data: false,
    };
  }

  const direction = adjustment === "up" ? "up" : "down";
  return {
    recommended_size,
    recommendation_type: "adjustment",
    recommendation_text: `Size ${direction} to ${recommended_size} from your usual ${ctx.user_size}`,
    reasoning: reasoningForAdjustment(
      adjustment,
      source,
      ctx.fit_type,
      ctx.user_body_shape,
    ),
    confidence_level,
    needs_user_data: false,
  };
}

// ---------------------------------------------------------------------------
// Test snippets (Cases A–E from spec). These are comment-form expectations;
// formal unit tests can be added under a __tests__ directory in a later pass.
// ---------------------------------------------------------------------------
// Case A: { user_size: null, ... } →
//   type: "prompt_user", text: "Tell us your usual size...",
//   confidence: "low", needs_user_data: true
// Case B: { user_size: "M", fit_type: "slim" } →
//   recommended: "M", type: "absolute", text: "Buy size M",
//   reasoning: "Slim cut — confirm in the size guide", confidence: "low"
// Case C: { user_size: "M", fit_type: "slim", fit_copy: "Runs small..." } →
//   recommended: "L", type: "adjustment",
//   text: "Size up to L from your usual M", confidence: "medium"
// Case D (Build 1.1): { user_size: "M", user_body_shape: "athletic",
//   fit_type: "slim", fit_copy: "Fits true to size", size_chart: {...} } →
//   fit_copy_locks_size = true → shape rule skipped.
//   recommended_size: "M", type: "absolute", text: "Buy size M",
//   reasoning: "Fits true to size — your usual size works",
//   base confidence "medium-high" → bumped to "high".
// Case E: { user_size: "L", fit_type: null, fit_copy: null, size_chart: null } →
//   recommended: "L", type: "hedged", text: "Buy your usual size L",
//   confidence: "low"

// ===========================================================================
// Unified Confidence Assessment (Build 3e)
// ---------------------------------------------------------------------------
// Single source of truth. Combines garment data + user data + brand fit
// signal (via lookupBrandFit) into one assessment with one confidence value.
// Replaces the dual `generateRecommendation` / `generateReasoning` calls
// that produced conflicting confidence numbers.
//
// Formula: confidence_score = min(100, sqrt(garment × user) + brand_fit / 2)
// ===========================================================================

import {
  lookupBrandFit,
  type BrandFitLookupResult,
  type UserBrandFitEntry,
} from "./brandFitLookup";
import { resolveSizeKey } from "./sizeChartResolver";
import { retailerIdFromLink } from "./retailerSizeConvention";
import { isSpaIncompatible } from "./brandGuideConfig";
import {
  normaliseFitType,
  logDataQualityIssue,
  type FashionAIFitType,
} from "./fitTypeNormalisation";
import {
  magnitudeToOffset,
  type MagnitudeBand,
  type MagnitudeMapping,
  type CmOffset,
} from "./magnitudeTranslation";

export type { UserBrandFitEntry, BrandFitLookupResult } from "./brandFitLookup";

/**
 * Sub-build 2b — per-brand fit_type modifier entry sourced from
 * `brand_fit_profiles.fit_type_modifiers` JSONB. May contain extra fields;
 * only `fit_type` + `magnitude` are required here.
 */
export interface FitTypeModifier {
  fit_type: string;
  direction?: string;
  magnitude: MagnitudeBand;
  ease_descriptor?: string;
  styling_implication?: string;
}

/**
 * Sub-build 2a — brand-level fit tendency profile fetched from
 * `brand_fit_profiles`. Optional; null when no profile exists for the
 * brand/gender/category combination. Sub-build 2b extends with
 * `fit_type_modifiers` for item-specific copy.
 */
export interface BrandFitProfile {
  retailer: string;
  brand_name: string;
  baseline_fit_tendency: "runs_small" | "true_to_size" | "runs_large";
  confidence: "high" | "medium" | "low";
  fit_type_modifiers?: FitTypeModifier[];
}

export interface ConfidenceContext {
  // Garment data (per-product)
  fit_type: string | null;
  fabric_composition: string | null;
  category: string | null;
  size_chart: Record<string, any> | null;
  fit_copy: string | null;
  model_reference: { size_worn?: string | null; model_height?: string | null } | null;
  retailer: string | null;

  // Build C: brand-level authoritative size chart (overrides size_chart for
  // measurements when present). Caller MAY supply it; otherwise
  // `generateConfidenceAssessment` looks it up via the supabase client.
  brand_size_chart?: Record<string, any> | null;

  // Sub-build 3: confidence tier for the brand_size_chart (drives whether
  // we surface specific cm or qualitative copy). Populated alongside
  // `brand_size_chart` by `generateConfidenceAssessment`.
  brand_size_chart_confidence?: "high" | "medium" | "low" | "estimate" | null;

  // Sub-build 2a/2b: brand-level fit tendency profile (with modifiers).
  // Caller MAY supply it; otherwise `generateConfidenceAssessment` looks it up.
  brand_fit_profile?: BrandFitProfile | null;

  // Sub-build 2b: active magnitude→cm mapping. Caller MAY supply; otherwise
  // looked up via the supabase client.
  magnitude_mapping?: MagnitudeMapping | null;

  // User profile
  user_size: string | null;
  /** Build B: women's UK numeric size (e.g. "10", "12"). Optional. */
  user_size_uk_number?: string | null;
  user_height: string | null;
  user_body_shape: string | null;
  user_body_size: string | null;
  user_gender: Gender | null;

  // Sub-build 1.5: gender resolution for brand-table joins. Sourced from
  // `garment_data.gender`. When 'men' or 'women' this wins over user_gender
  // for `brand_fit_profiles` / `brand_size_charts` lookups; 'unisex' / null
  // falls back to user_gender. Fixes cross-gender shopping at mixed-gender
  // retailers (Next, ASOS, John Lewis, etc.).
  garment_gender?: "men" | "women" | "unisex" | null;

  // Sub-build 1.5 Part 2: explicit per-outfit user gender override (set via
  // "Switch model" UX). Consulted only when `garment_gender` is null/unisex.
  // Currently only set for authenticated users; null for guests.
  gender_override?: "men" | "women" | "unisex" | "non-binary" | null;

  // Brand fit user verdicts
  user_brand_fit_tops: UserBrandFitEntry[];
  user_brand_fit_bottoms: UserBrandFitEntry[];

  // Optional signals from the `fit-confidence` edge fn (auth users only).
  edge_tier_signal?: EdgeTierSignal | null;

  // A-7a PR 5c — structured garment attributes plumbed from detected_items
  // (7 vision-extracted enums) + garment_data.fabric_stretch_enum. Pure
  // pass-through into ResolvedSignals.garment_attributes; the
  // T2_GARMENT_ATTRIBUTES scoring tier does not consume these yet.
  silhouette?: string | null;
  length?: string | null;
  neckline?: string | null;
  sleeve_length?: string | null;
  fit?: string | null;
  fabric_weight?: string | null;
  structure?: string | null;
  fabric_stretch_enum?: "none" | "light" | "moderate" | "high" | null;

  // A-7b PR 6a — display name for styling row {item} interpolation.
  // Sourced from detected_items.name (primary) → garment_data.product_name
  // (secondary, not yet plumbed via RPC). Normalised by `normalizeItemName`
  // in PR 6b before being interpolated into the styling row copy.
  item_name?: string | null;
}



/**
 * Minimal subset of the `fit-confidence` edge fn per-tier response shape that
 * the client engine consumes. Kept loose to tolerate partial payloads.
 */
export interface EdgeTierSignal {
  confidence_level?: "low" | "medium" | "medium-high" | "high";
  recommended_size?: string | null;
  recommended_size_note?: string | null;
  positive_rows?: string[];
  warning_rows?: string[];
  based_on?: string[];
  limited_by?: string[];
  data_sources?: {
    has_profile?: boolean;
    has_garment_data?: boolean;
    has_brand_fit_signal?: boolean;
    has_size_chart?: boolean;
    has_outcomes?: boolean;
    outcome_count?: number;
  } | null;
}

export interface MissingField {
  field:
    | "size"
    | "size_number"
    | "height"
    | "body_shape"
    | "body_size"
    | "body_build";
  display_label: string;
  priority: "high" | "medium" | "low";
}

export type PrimaryGap =
  | "user_data"
  | "garment_data"
  | "brand_fit_conflict"
  | "none";

export interface ConfidenceAssessment {
  recommended_size: string | null;
  recommendation_type: "absolute" | "adjustment" | "hedged" | "prompt_user";
  recommendation_text: string;
  reasoning: string;

  confidence_level: "low" | "medium" | "medium-high" | "high";
  confidence_score: number; // 0-100

  garment_score: number; // 0-50
  user_score: number; // 0-50
  brand_fit_score: number; // 0-50

  primary_gap: PrimaryGap;
  missing_fields: MissingField[];
  diagnostic_message: string;

  reasoning_rows: ReasoningRow[];
  brand_fit_signal: BrandFitLookupResult | null;

  // A-7b PR 6c-3 — LLM styling-note fallback metadata. When `styling_pending`
  // is true, PurchaseConfidenceCard asynchronously calls generate-styling-note
  // and appends a sparkle row on success. Always optional / nullable.
  styling_pending?: boolean;
  styling_llm_payload?: StylingLlmPayload | null;

  // SKIP-FLOOR-1 — ADD-only. Surfaced so the card can derive verdict via
  // verdictFromAssessment (floors Skip when no fit-predictive tier fired).
  // Empty array in legacy path; populated by unifiedScoring engine.
  tiers_fired?: string[];

  // PQ-5c — ADD-only. Lens system v1 (Fit + Suits you populated; Wardrobe +
  // You'll wear reserved for PQ-5d / PQ-5e). When absent, PCC falls back to
  // the legacy reasoning_rows render path + restored verdict-block reason
  // and styling lines.
  lenses?: import("./unifiedScoring").LensSystem | null;
}


export interface StylingLlmPayload {
  garment_attributes: Record<string, string | null>;
  item_name: string | null;
  category: string | null;
  body_shape: string | null;
  body_size: string | null;
  height_bucket: "petite" | "regular" | "tall" | null;
  brand_fit_tendency: "runs_small" | "runs_large" | "true_to_size" | null;
}

// ---- scoring -------------------------------------------------------------

function hasRealMeasurements(chart: Record<string, any> | null): boolean {
  if (!chart || typeof chart !== "object") return false;
  for (const row of Object.values(chart)) {
    if (row && typeof row === "object") {
      for (const v of Object.values(row as Record<string, unknown>)) {
        if (
          v != null &&
          typeof v !== "undefined" &&
          String(v).trim() !== "" &&
          String(v).toLowerCase() !== "null"
        ) {
          return true;
        }
      }
    }
  }
  return false;
}

function scoreGarmentData(ctx: ConfidenceContext): number {
  let score = 0;
  // Build B: only credit the size_chart slot when the chart is usable —
  // it has real measurements AND the user has a size in the matching convention.
  if (ctx.size_chart && hasRealMeasurements(ctx.size_chart)) {
    const res = resolveSizeKey(ctx.size_chart, {
      size_top_letter: ctx.user_size,
      size_top_uk_number: ctx.user_size_uk_number ?? null,
    });
    if (res.matched) score += 20;
  }
  if (ctx.fit_copy && ctx.fit_copy.trim().length > 5) score += 15;
  if (ctx.fit_type) score += 10;
  if (
    ctx.model_reference &&
    ctx.model_reference.size_worn &&
    ctx.model_reference.model_height
  )
    score += 10;
  if (ctx.fabric_composition && ctx.fabric_composition.trim().length > 0)
    score += 5;
  return Math.min(50, score);
}

function scoreUserData(ctx: ConfidenceContext): number {
  let score = 0;
  if (ctx.user_size) score += 20;
  if (ctx.user_height) score += 10;
  if (ctx.user_body_shape) score += 10;
  if (ctx.user_gender === "men") {
    // body_build (stored in body_shape slot for men) covers shape+size, weight 20
    if (ctx.user_body_shape) score += 10;
  } else {
    if (ctx.user_body_size) score += 10;
  }
  return Math.min(50, score);
}

function scoreBrandFit(signal: BrandFitLookupResult | null): number {
  if (!signal || !signal.has_signal) return 0;
  if (signal.conflict_detected) return 0;
  switch (signal.source) {
    case "direct":
      return 50;
    case "both_similar":
      return 40;
    case "curated_similar":
    case "measured_similar":
      return 30;
    default:
      return 0;
  }
}

function scoreToConfidenceLevel(
  score: number,
): "low" | "medium" | "medium-high" | "high" {
  if (score >= 76) return "high";
  if (score >= 51) return "medium-high";
  if (score >= 26) return "medium";
  return "low";
}

// ---- diagnosis -----------------------------------------------------------

function diagnoseGap(
  ctx: ConfidenceContext,
  garment_score: number,
  user_score: number,
  brand_fit: BrandFitLookupResult | null,
): { primary_gap: PrimaryGap; missing_fields: MissingField[] } {
  const missing_fields: MissingField[] = [];

  if (!ctx.user_size) {
    missing_fields.push({
      field: "size",
      display_label: "your usual size",
      priority: "high",
    });
  } else if (ctx.size_chart) {
    // Build B: surface a numeric-size CTA when the active chart requires it.
    const res = resolveSizeKey(ctx.size_chart, {
      size_top_letter: ctx.user_size,
      size_top_uk_number: ctx.user_size_uk_number ?? null,
    });
    if (
      !res.matched &&
      res.reason === "no_user_size" &&
      res.missing_field === "number" &&
      ctx.user_gender !== "men"
    ) {
      missing_fields.push({
        field: "size_number",
        display_label: "your UK number size",
        priority: "high",
      });
      try {
        console.log(
          `[recommendation] missing_field surfaced: field=size_number convention=${res.convention}`,
        );
      } catch {
        /* ignore */
      }
    }
  }
  if (!ctx.user_height) {
    missing_fields.push({
      field: "height",
      display_label: "your height",
      priority: "medium",
    });
  }
  if (!ctx.user_body_shape) {
    missing_fields.push({
      field: ctx.user_gender === "men" ? "body_build" : "body_shape",
      display_label:
        ctx.user_gender === "men" ? "your build" : "your body shape",
      priority: "medium",
    });
  }
  if (ctx.user_gender !== "men" && !ctx.user_body_size) {
    missing_fields.push({
      field: "body_size",
      display_label: "your body size",
      priority: "low",
    });
  }

  if (brand_fit?.conflict_detected) {
    return { primary_gap: "brand_fit_conflict", missing_fields };
  }
  if (garment_score < 20 && user_score >= 30) {
    return { primary_gap: "garment_data", missing_fields };
  }
  if (user_score < 30 && garment_score >= 20) {
    return { primary_gap: "user_data", missing_fields };
  }
  if (garment_score < 20 && user_score < 30) {
    return { primary_gap: "user_data", missing_fields };
  }
  return { primary_gap: "none", missing_fields };
}

function buildDiagnosticMessage(
  primary_gap: PrimaryGap,
  missing_fields: MissingField[],
  _brand_fit: BrandFitLookupResult | null,
  confidence_level: "low" | "medium" | "medium-high" | "high",
): string {
  if (confidence_level === "high" || confidence_level === "medium-high") {
    return "";
  }
  if (primary_gap === "brand_fit_conflict") {
    return "Your brand fit data gives mixed signals for this retailer — we can't confidently adjust the size.";
  }
  if (primary_gap === "garment_data") {
    // Footer copy removed (Phase A-5) — subtitle copy already covers the
    // "check the size guide" guidance. Avoid double-undermining the headline.
    return "";
  }
  if (primary_gap === "user_data") {
    const high = missing_fields.filter((f) => f.priority === "high");
    if (high.length > 0) {
      return `Tell us ${high[0].display_label} to get a recommendation.`;
    }
    const medium = missing_fields
      .filter((f) => f.priority === "medium")
      .slice(0, 2);
    if (medium.length === 1) {
      return `Add ${medium[0].display_label} to improve confidence.`;
    }
    if (medium.length >= 2) {
      return `Add ${medium[0].display_label} and ${medium[1].display_label} to improve confidence.`;
    }
  }
  return "";
}

// ---- recommendation ------------------------------------------------------

function verdictDirection(v: BrandFitVerdict): "up" | "down" | "" {
  if (v === "runs_small") return "up";
  if (v === "runs_large") return "down";
  return "";
}

type BrandFitVerdict = "true_to_size" | "runs_small" | "runs_large";

function applyBrandFitVerdict(size: string, verdict: BrandFitVerdict): string {
  const order = SIZE_ORDER;
  const idx = order.indexOf(size as (typeof SIZE_ORDER)[number]);
  if (idx === -1) return size;
  if (verdict === "runs_small")
    return order[Math.min(order.length - 1, idx + 1)];
  if (verdict === "runs_large") return order[Math.max(0, idx - 1)];
  return size;
}

function buildRecommendation(
  ctx: ConfidenceContext,
  brand_fit: BrandFitLookupResult | null,
): {
  recommended_size: string | null;
  recommendation_type: ConfidenceAssessment["recommendation_type"];
  recommendation_text: string;
  reasoning: string;
} {
  if (!ctx.user_size) {
    return {
      recommended_size: null,
      recommendation_type: "prompt_user",
      recommendation_text: "Tell us your size to get a recommendation",
      reasoning: "We need your usual size to recommend a fit",
    };
  }

  // fit_copy locking is the strongest garment-level signal — overrides brand fit.
  const fitCopyLocks = fitCopyLocksSize(ctx.fit_copy);

  if (!fitCopyLocks && brand_fit?.has_signal && !brand_fit.conflict_detected && brand_fit.verdict) {
    const adjusted = applyBrandFitVerdict(ctx.user_size, brand_fit.verdict);
    if (adjusted !== ctx.user_size) {
      return {
        recommended_size: adjusted,
        recommendation_type: "adjustment",
        recommendation_text: `Size ${verdictDirection(brand_fit.verdict)} to ${adjusted} from your usual ${ctx.user_size}`,
        reasoning: brand_fit.reasoning,
      };
    }
    return {
      recommended_size: ctx.user_size,
      recommendation_type: "absolute",
      recommendation_text: `Buy size ${ctx.user_size}`,
      reasoning: brand_fit.reasoning,
    };
  }

  // Fall back to fit_copy + shape logic (Build 1/1.1 behaviour).
  const copyAdj: Adjustment = fitCopyLocks ? "none" : parseFitCopy(ctx.fit_copy);
  let adjustment: Adjustment = copyAdj;
  let source: "fit_copy" | "shape" = "fit_copy";
  if (!fitCopyLocks && adjustment === "none") {
    const shapeAdj = shapeAdjustmentForFit(
      ctx.fit_type,
      ctx.user_body_shape,
      ctx.user_body_size,
      ctx.category,
    );
    if (shapeAdj !== "none") {
      adjustment = shapeAdj;
      source = "shape";
    }
  }

  const recommended_size =
    adjustment === "none"
      ? ctx.user_size
      : adjustSize(ctx.user_size, adjustment);

  if (fitCopyLocks) {
    return {
      recommended_size,
      recommendation_type: "absolute",
      recommendation_text: `Buy size ${recommended_size}`,
      reasoning: "Fits true to size — your usual size works",
    };
  }
  if (adjustment === "none") {
    // No useful garment signal at all → hedge with profile-only copy.
    if (!ctx.fit_type && !ctx.fit_copy && !ctx.size_chart) {
      const isDress = (ctx.category ?? "").toLowerCase() === "dress";
      const hasBrandFitSignal = !!(brand_fit?.has_signal && !brand_fit.conflict_detected);
      const hasBrandChart = !!ctx.brand_size_chart;
      const hasBrandProfile = !!ctx.brand_fit_profile;
      const hasRetailer =
        !!ctx.retailer &&
        ctx.retailer.trim() !== "" &&
        ctx.retailer.toLowerCase() !== "unknown";
      // Scenario 4: truly unknown retailer — no chart, no profile, no fit signal.
      const isUnknownRetailer =
        hasRetailer && !hasBrandFitSignal && !hasBrandProfile && !hasBrandChart;
      // Scenario 5: known retailer, brand_size_chart hit but no fit-tendency profile.
      const isKnownChartOnly =
        hasRetailer && !hasBrandFitSignal && !hasBrandProfile && hasBrandChart;

      const retailerLabel = hasRetailer ? (ctx.retailer as string).trim() : "";

      let headline: string;
      if (isKnownChartOnly) {
        headline = `Likely size ${ctx.user_size} — based on ${retailerLabel}'s size chart`;
      } else if (isDress) {
        headline = `Likely size ${ctx.user_size} — your usual fit for dresses`;
      } else {
        headline = `Likely size ${ctx.user_size} — your usual fit`;
      }

      let reasoning: string;
      if (isUnknownRetailer) {
        reasoning = `This brand isn't in our fit database yet. Your size ${ctx.user_size} is a safe starting point — check the retailer's size guide for their fit notes.`;
      } else if (isKnownChartOnly) {
        reasoning = `${retailerLabel} confirms size ${ctx.user_size} for your measurements. Check the size guide if you need precise fit.`;
      } else if (isDress) {
        reasoning = `Dress lengths vary by brand and cut. Your size ${ctx.user_size} is a safe starting point — check the size guide if you're between sizes.`;
      } else {
        reasoning = `Your size ${ctx.user_size} is a safe starting point. Length and cut vary by brand — check the retailer's size guide if exact fit matters.`;
      }

      return {
        recommended_size,
        recommendation_type: "hedged",
        recommendation_text: headline,
        reasoning,
      };
    }
    return {
      recommended_size,
      recommendation_type: "absolute",
      recommendation_text: `Buy size ${recommended_size}`,
      reasoning: reasoningForAbsolute(ctx.fit_type),
    };
  }
  const dir = adjustment === "up" ? "up" : "down";
  return {
    recommended_size,
    recommendation_type: "adjustment",
    recommendation_text: `Size ${dir} to ${recommended_size} from your usual ${ctx.user_size}`,
    reasoning: reasoningForAdjustment(
      adjustment,
      source,
      ctx.fit_type,
      ctx.user_body_shape,
    ),
  };
}


/**
 * Maps a warning row's free text to a short acknowledgment suffix appended
 * to the hedged recommendation copy. Keeps the headline coherent when a
 * profile-only warning is the only signal we have.
 */
function warningAcknowledgment(text: string): string {
  const t = text.toLowerCase();
  if (/run (short|long)|length|midi/.test(t)) return "but check length";
  if (/bust/.test(t)) return "but check bust fit";
  if (/hip/.test(t)) return "but check hip fit";
  if (/loose|swamp|overwhelm/.test(t)) return "but it may run loose";
  if (/tight|pull|narrow/.test(t)) return "but it may run tight";
  return "but read the warnings below";
}

// ---- reasoning rows ------------------------------------------------------

function buildReasoningRows(
  ctx: ConfidenceContext,
  brand_fit: BrandFitLookupResult | null,
): ReasoningRow[] {
  // Reuse legacy generateReasoning for the body of supporting rows, then
  // optionally prepend a brand-fit row when available.
  const legacy = generateReasoning({
    fit_type: ctx.fit_type,
    fabric_composition: ctx.fabric_composition,
    category: ctx.category,
    size_in_chart: !!(
      ctx.size_chart &&
      ctx.user_size &&
      Object.keys(ctx.size_chart).some(
        (k) => k.toUpperCase() === (ctx.user_size as string).toUpperCase(),
      )
    ),
    size: ctx.user_size,
    height: (ctx.user_height as any) ?? null,
    body_shape: ctx.user_body_shape,
    body_size: ctx.user_body_size,
    gender: ctx.user_gender,
    retailer_name: ctx.retailer,
  });

  const rows: ReasoningRow[] = [...legacy.rows];

  if (brand_fit?.has_signal && !brand_fit.conflict_detected) {
    rows.unshift({
      icon: brand_fit.verdict === "true_to_size" ? "check" : "info",
      text: brand_fit.reasoning,
    });
  } else if (brand_fit?.conflict_detected) {
    rows.unshift({ icon: "warning", text: brand_fit.reasoning });
  }
  return rows;
}

// ---- main entry ----------------------------------------------------------

/**
 * Sub-build 2b — generate locked-copy advisory based on fit_type + magnitude.
 * Returns null when no template matches.
 */
function generateModifierCopy(params: {
  fitType: FashionAIFitType;
  magnitude: MagnitudeBand;
  brandName: string;
}): string | null {
  const { fitType, magnitude, brandName } = params;

  if (fitType === "slim") {
    if (magnitude === "slightly")
      return `This is cut close — runs a touch slimmer than ${brandName}'s usual fit.`;
    if (magnitude === "moderately")
      return `This is a closer cut — consider sizing up if you prefer comfort over fitted.`;
    if (magnitude === "significantly")
      return `This is a significantly fitted cut — often sized up for comfort.`;
    if (magnitude === "dramatically")
      return `Very body-conscious cut — typically requires sizing up.`;
  }
  if (fitType === "relaxed") {
    if (magnitude === "slightly")
      return `This relaxed cut sits a little roomier than ${brandName}'s usual fit.`;
    if (magnitude === "moderately")
      return `This relaxed cut runs noticeably looser — consider sizing down if you want a closer fit.`;
    if (magnitude === "significantly")
      return `This is a significantly relaxed cut — most wearers find it roomier than expected.`;
    if (magnitude === "dramatically")
      return `This is a deliberately voluminous cut — typically sized down one or two.`;
  }
  if (fitType === "oversized") {
    if (magnitude === "slightly")
      return `This oversized cut runs roomier than ${brandName}'s usual fit.`;
    if (magnitude === "moderately")
      return `This oversized cut runs noticeably looser — consider sizing down if you want a closer fit.`;
    if (magnitude === "significantly")
      return `This is a significantly oversized cut — most wearers size down one.`;
    if (magnitude === "dramatically")
      return `This is a deliberately voluminous cut — typically sized down one or two.`;
  }
  if (fitType === "cropped") {
    if (magnitude === "slightly") return `Sits slightly above the waist.`;
    if (magnitude === "moderately") return `Cropped — hits above the waist.`;
    if (magnitude === "significantly")
      return `Notably cropped — sits high on the torso.`;
    if (magnitude === "dramatically")
      return `Very cropped — designed to sit at or above the underbust.`;
  }
  if (fitType === "longline") {
    if (magnitude === "slightly") return `Runs a little longer in the body.`;
    if (magnitude === "moderately") return `Longer cut — drapes past the hip.`;
    if (magnitude === "significantly")
      return `Longline cut — covers the hip well.`;
    if (magnitude === "dramatically")
      return `Tunic-length cut — sits at mid-thigh.`;
  }
  if (fitType === "muscle") {
    if (magnitude === "slightly")
      return `This is a slightly fitted athletic cut — typically true to size.`;
    if (magnitude === "moderately")
      return `This is a fitted athletic cut — typically size up unless you have a lean/athletic build.`;
    if (magnitude === "significantly")
      return `This is a heavily fitted muscle cut — typically size up unless gym-trained.`;
    if (magnitude === "dramatically")
      return `This is an extreme muscle fit — built around very athletic builds; consider sizing up two.`;
  }
  return null;
}

/**
 * Sub-build 2b — finds the brand modifier for the scraped fit_type, if any.
 * Returns null when normalisation fails or the brand doesn't have a modifier
 * for this fit_type.
 */
function findItemFitTypeModifier(
  ctx: ConfidenceContext,
): { fitType: FashionAIFitType; modifier: FitTypeModifier } | null {
  const profile = ctx.brand_fit_profile;
  if (!profile || !profile.fit_type_modifiers || !ctx.fit_type) return null;
  const normalised = normaliseFitType(ctx.fit_type);
  if (!normalised.matched) return null;
  const modifier = profile.fit_type_modifiers.find(
    (m) => m.fit_type === normalised.fit_type,
  );
  if (!modifier) return null;
  return { fitType: normalised.fit_type, modifier };
}

/**
 * Sub-build 2b — surface item-specific fit_type modifier warning.
 * Supersedes the 2a brand-tendency row when both could fire.
 */
function surfaceItemFitTypeModifier(
  ctx: ConfidenceContext,
): ReasoningRow | null {
  const found = findItemFitTypeModifier(ctx);
  if (!found || !ctx.brand_fit_profile) return null;
  // Magnitude mapping is required for the math even if we don't surface
  // numeric offsets in copy — keeps signal aligned with engine math.
  if (!ctx.magnitude_mapping) return null;
  try {
    // Compute offset for downstream/debug parity (not surfaced in copy).
    magnitudeToOffset(found.fitType, found.modifier.magnitude, ctx.magnitude_mapping);
  } catch {
    return null;
  }
  const text = generateModifierCopy({
    fitType: found.fitType,
    magnitude: found.modifier.magnitude,
    brandName: ctx.brand_fit_profile.brand_name,
  });
  if (!text) return null;
  return { icon: "warning", text };
}

/**
 * Sub-build 2a — surface a brand-level fit-tendency warning when
 * `brand_fit_profile` is non-null, tendency != true_to_size, and confidence
 * is medium or high. Sub-build 2b adds suppression when an item-level
 * modifier will fire (item-specific signal wins).
 */
function surfaceBrandFitTendency(
  ctx: ConfidenceContext,
  recommendation: { recommended_size: string | null; recommendation_type: ConfidenceAssessment["recommendation_type"] },
): ReasoningRow | null {
  const profile = ctx.brand_fit_profile;
  if (!profile) return null;
  if (profile.baseline_fit_tendency === "true_to_size") return null;
  if (profile.confidence === "low") return null;

  // Sub-build 2b — suppress when item-level modifier will fire.
  if (findItemFitTypeModifier(ctx) && ctx.magnitude_mapping) return null;

  // Dedupe: if the recommendation already advises stepping the same direction
  // (via shape/profile/user-brand-fit rules), suppress the duplicate row.
  const tendency = profile.baseline_fit_tendency;
  const usual = ctx.user_size;
  if (
    recommendation.recommendation_type === "adjustment" &&
    usual &&
    recommendation.recommended_size
  ) {
    const adjUp = adjustSize(usual, "up");
    const adjDown = adjustSize(usual, "down");
    if (tendency === "runs_small" && recommendation.recommended_size === adjUp) return null;
    if (tendency === "runs_large" && recommendation.recommended_size === adjDown) return null;
  }

  const { brand_name, confidence } = profile;
  let text: string | null = null;
  if (tendency === "runs_small" && confidence === "high")
    text = `${brand_name} typically runs small — consider sizing up.`;
  else if (tendency === "runs_small" && confidence === "medium")
    text = `${brand_name} often runs small for fitted styles.`;
  else if (tendency === "runs_large" && confidence === "high")
    text = `${brand_name} typically runs large — consider sizing down.`;
  else if (tendency === "runs_large" && confidence === "medium")
    text = `${brand_name} often runs roomier than expected.`;
  if (!text) return null;
  return { icon: "warning", text };
}

/**
 * Sub-build 3 — surface a measurement-context body row for the resolved
 * brand size chart. Same-brand only; no cross-brand math. Confidence-tiered:
 *   • medium/high → specific cm copy ("Size M at COS typically has a chest 86cm and waist 68cm.")
 *   • low         → qualitative copy ("Your usual size M should map to Brand's standard M sizing.")
 *   • no chart / size key missing / no recommendation → silent
 * Surfaces context only — does NOT alter the recommendation.
 */
function surfaceMeasurementContext(
  ctx: ConfidenceContext,
  recommendation: { recommended_size: string | null },
): ReasoningRow | null {
  const chart = ctx.brand_size_chart;
  if (!chart || typeof chart !== "object") return null;
  const confidence = ctx.brand_size_chart_confidence ?? "estimate";
  const sizeKey = recommendation.recommended_size;
  if (!sizeKey) return null;
  const measurements = (chart as Record<string, any>)[sizeKey];
  if (!measurements || typeof measurements !== "object") return null;

  const brandName =
    ctx.brand_fit_profile?.brand_name ??
    (ctx.retailer ? ctx.retailer.charAt(0).toUpperCase() + ctx.retailer.slice(1) : "this brand");

  if (confidence === "medium" || confidence === "high") {
    const chest = numericOrNull(measurements.chest);
    const waist = numericOrNull(measurements.waist);
    const parts: string[] = [];
    if (chest != null) parts.push(`chest ${chest}cm`);
    if (waist != null) parts.push(`waist ${waist}cm`);
    let text: string;
    if (parts.length === 0) {
      text = `Your usual size ${sizeKey} maps to ${brandName}'s standard ${sizeKey} cut.`;
    } else if (parts.length === 1) {
      text = `Size ${sizeKey} at ${brandName} typically has a ${parts[0]}.`;
    } else {
      text = `Size ${sizeKey} at ${brandName} typically has a ${parts.join(" and ")}.`;
    }
    return { icon: "info", text };
  }

  // Sub-build 3 follow-up: low/estimate confidence → silent fall-through to
  // 2a/2b/Build A+B reasoning (qualitative copy was tautological).
  return null;
}

function numericOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}



/**
 * Phase A-6 PR 2 — feature flag for the unified scoring pipeline.
 * Defaulted FALSE; PR 3 flips after manual device verification. When TRUE,
 * `generateConfidenceAssessment` short-circuits to the new pipeline
 * (resolveSignals → computeFitScore → generateCardContent). When FALSE,
 * legacy code path runs unchanged.
 */
export const UNIFIED_SCORING = true;

const CONFIDENCE_DEBUG_KEY = "l2f_confidence_debug";

function shouldLogConfidenceDebug(): boolean {
  try {
    return (
      globalThis.localStorage?.getItem(CONFIDENCE_DEBUG_KEY) === "1" ||
      (typeof globalThis.location !== "undefined" &&
        new URLSearchParams(globalThis.location.search).get(CONFIDENCE_DEBUG_KEY) === "1")
    );
  } catch {
    return false;
  }
}

function confidenceDebug(label: string, payload: Record<string, unknown>): void {
  if (shouldLogConfidenceDebug()) console.log(`[confidence-debug] ${label}`, payload);
}

export async function generateConfidenceAssessment(
  ctxInput: ConfidenceContext,
  // deno-lint-ignore no-explicit-any
  supabase?: any,
): Promise<ConfidenceAssessment> {
  // Unified pipeline short-circuit moved BELOW brand_size_charts +
  // brand_fit_profiles lookups + edge merge (PR 3b). Resolved at end of fn.



  // Build C — brand-level size chart lookup. When a supabase client is
  // provided and the caller hasn't supplied a brand_size_chart, look one
  // up by retailer + gender + category. Brand-level chart is authoritative
  // for measurements; per-product fit_type/fit_copy/model_reference unchanged.
  const ctx: ConfidenceContext = { ...ctxInput };
  const normCat: "tops" | "bottoms" | "dresses" =
    (ctx.category ?? "").toLowerCase() === "bottom"
      ? "bottoms"
      : (ctx.category ?? "").toLowerCase() === "dress"
        ? "dresses"
        : "tops";
  const retailerId = ctx.retailer ? retailerIdFromLink(ctx.retailer) ?? ctx.retailer.toLowerCase() : null;

  let chart_source: "brand_level" | "per_product" | "none" = ctx.size_chart
    ? "per_product"
    : "none";
  let brand_size_chart_convention:
    | "numeric"
    | "letter"
    | "mixed"
    | null = null;


  // Sub-build 1.5 Part 2 — effective gender for brand-table joins.
  // Priority: garment_data.gender (men|women) > explicit user override
  // (men|women) > profile gender. Falls back to null when none available.
  const garmentGender = ctx.garment_gender;
  const overrideGender = ctx.gender_override;
  const profileGender = ctx.user_gender;
  const effectiveGender: "men" | "women" | null =
    garmentGender === "men" || garmentGender === "women"
      ? garmentGender
      : overrideGender === "men" || overrideGender === "women"
        ? overrideGender
        : profileGender === "men"
          ? "men"
          : profileGender === "women" || profileGender === "non-binary"
            ? "women"
            : null;
  console.log("[gender-resolution]", {
    retailer: retailerId,
    garmentGender: garmentGender ?? null,
    overrideGender: overrideGender ?? null,
    profileGender: profileGender ?? null,
    effectiveGender,
  });
  confidenceDebug("generateConfidenceAssessment.brand_lookup_gates", {
    effectiveGender,
    retailerId,
    normCat,
    hasSupabase: !!supabase,
    hasInputBrandSizeChart: ctx.brand_size_chart != null,
    hasInputBrandFitProfile: ctx.brand_fit_profile != null,
    spaIncompatible: retailerId ? isSpaIncompatible(retailerId) : null,
  });


  if (
    ctx.brand_size_chart == null &&
    supabase &&
    retailerId &&
    !isSpaIncompatible(retailerId) &&
    effectiveGender
  ) {
    try {
      const { data: brandRow } = await supabase
        .from("brand_size_charts")
        .select("size_chart, convention, source_url, confidence")
        .eq("retailer", retailerId)
        .eq("gender", effectiveGender)
        .eq("category", normCat)
        .eq("scrape_status", "success")
        .maybeSingle();
      if (brandRow?.size_chart) {
        ctx.brand_size_chart = brandRow.size_chart as Record<string, any>;
        ctx.brand_size_chart_confidence = (brandRow.confidence ?? null) as
          | "high" | "medium" | "low" | "estimate" | null;
        const conv = brandRow.convention;
        if (conv === "numeric" || conv === "letter" || conv === "mixed") {
          brand_size_chart_convention = conv;
        }
      }
      confidenceDebug("generateConfidenceAssessment.brand_size_charts_result", {
        executed: true,
        effectiveGender,
        retailerId,
        normCat,
        brandRow: brandRow ?? null,
        ctxBrandSizeChartPopulated: ctx.brand_size_chart != null,
      });

    } catch (e) {
      console.warn("[confidence] brand_size_charts lookup failed", e);
    }
  } else {
    confidenceDebug("generateConfidenceAssessment.brand_size_charts_result", {
      executed: false,
      effectiveGender,
      retailerId,
      normCat,
      reason: {
        hasExistingChart: ctx.brand_size_chart != null,
        hasSupabase: !!supabase,
        hasRetailerId: !!retailerId,
        spaCompatible: retailerId ? !isSpaIncompatible(retailerId) : null,
        hasEffectiveGender: !!effectiveGender,
      },
      ctxBrandSizeChartPopulated: ctx.brand_size_chart != null,
    });
  }

  // Sub-build 2a — brand fit tendency lookup. Independent of size chart;
  // surface even when the chart is missing.
  if (
    ctx.brand_fit_profile == null &&
    supabase &&
    retailerId &&
    effectiveGender
  ) {
    try {
      const { data: bfp } = await supabase
        .from("brand_fit_profiles")
        .select(
          "retailer, brand_name, baseline_fit_tendency, confidence, fit_type_modifiers",
        )
        .eq("retailer", retailerId)
        .eq("gender", effectiveGender)
        .eq("category", normCat)
        .maybeSingle();
      if (bfp) ctx.brand_fit_profile = bfp as BrandFitProfile;
      confidenceDebug("generateConfidenceAssessment.brand_fit_profiles_result", {
        executed: true,
        effectiveGender,
        retailerId,
        normCat,
        bfp: bfp ?? null,
        ctxBrandFitProfilePopulated: ctx.brand_fit_profile != null,
      });
    } catch (e) {
      console.warn("[confidence] brand_fit_profiles lookup failed", e);
    }
  } else {
    confidenceDebug("generateConfidenceAssessment.brand_fit_profiles_result", {
      executed: false,
      effectiveGender,
      retailerId,
      normCat,
      reason: {
        hasExistingProfile: ctx.brand_fit_profile != null,
        hasSupabase: !!supabase,
        hasRetailerId: !!retailerId,
        hasEffectiveGender: !!effectiveGender,
      },
      ctxBrandFitProfilePopulated: ctx.brand_fit_profile != null,
    });
  }

  // Sub-build 2b — magnitude→cm mapping. Only fetch when we have a profile
  // and a per-product fit_type to translate; otherwise it's unused.
  // Sub-build 2 men's — gender-aware mapping version selection.
  if (
    ctx.magnitude_mapping == null &&
    supabase &&
    ctx.brand_fit_profile &&
    ctx.fit_type
  ) {
    try {
      const mappingVersion =
        effectiveGender === "men" ? "fashion_ai_2026_q1_men" : "fashion_ai_2026_q1";
      console.log("[mapping-version]", { effectiveGender, mappingVersion });
      const { data: mag } = await supabase
        .from("magnitude_cm_mapping")
        .select("mapping")
        .eq("active", true)
        .eq("version", mappingVersion)
        .maybeSingle();
      if (mag?.mapping) ctx.magnitude_mapping = mag.mapping as MagnitudeMapping;
    } catch (e) {
      console.warn("[confidence] magnitude_cm_mapping lookup failed", e);
    }
  }

  // Sub-build 2b — fire-and-forget data-quality logging for unmatched/ambiguous
  // scraped fit_type values, so future iterations can expand the Tier 1 map.
  if (supabase && ctx.fit_type) {
    const norm = normaliseFitType(ctx.fit_type);
    if (!norm.matched && (norm.reason === "unknown" || norm.reason === "ambiguous")) {
      logDataQualityIssue(supabase, {
        source: "fit_type_normalisation",
        reason: norm.reason,
        value: norm.original,
        retailer: retailerId ?? ctx.retailer ?? null,
      }).catch(() => {});
    }
  }

  if (ctx.brand_size_chart) {
    ctx.size_chart = ctx.brand_size_chart;
    chart_source = "brand_level";
  }

  try {
    console.log(
      `[engine-source] retailer=${retailerId ?? "unknown"} chart_source=${chart_source} ` +
        `fit_type_source=${ctx.fit_type ? "per_product" : "none"}`,
    );
  } catch {
    /* ignore */
  }

  // 1. Brand fit signal
  const cat: "tops" | "bottoms" = normCat === "bottoms" ? "bottoms" : "tops";
  const verdicts =
    cat === "bottoms" ? ctx.user_brand_fit_bottoms : ctx.user_brand_fit_tops;

  let brand_fit: BrandFitLookupResult | null = null;
  if (ctx.retailer) {
    try {
      brand_fit = await lookupBrandFit(
        ctx.retailer,
        verdicts ?? [],
        ctx.user_gender ?? "women",
        cat,
        supabase,
      );
    } catch (e) {
      console.warn("[confidence] brand fit lookup failed", e);
      brand_fit = null;
    }
  }


  // 2. Score
  const garment_score = scoreGarmentData(ctx);
  const user_score = scoreUserData(ctx);
  const brand_fit_score = scoreBrandFit(brand_fit);

  // 3. Combine
  const base = Math.sqrt(garment_score * user_score);
  const combined = Math.min(100, base + brand_fit_score / 2);
  const confidence_level = scoreToConfidenceLevel(combined);

  // 4. Diagnose
  const { primary_gap, missing_fields } = diagnoseGap(
    ctx,
    garment_score,
    user_score,
    brand_fit,
  );

  // 5. Recommendation
  const recommendation = buildRecommendation(ctx, brand_fit);

  // 6. Rows
  const reasoning_rows = buildReasoningRows(ctx, brand_fit);

  // 6-pre. Sub-build 3 — brand_size_charts measurement-context body row.
  // Positioned ABOVE 2a/2b warning rows. Same-brand only; advisory.
  const measurementRow = surfaceMeasurementContext(ctx, recommendation);
  if (measurementRow) reasoning_rows.push(measurementRow);

  // 6a. Sub-build 2a — brand-level fit tendency warning row, deduped against
  // any recommendation that already steps the user in the same direction.
  // Sub-build 2b suppression: if an item-level modifier will fire, 2a is silent.
  const tendencyRow = surfaceBrandFitTendency(ctx, recommendation);
  if (tendencyRow) reasoning_rows.push(tendencyRow);

  // 6a-bis. Sub-build 2b — item-specific fit_type modifier (supersedes 2a).
  const itemModifierRow = surfaceItemFitTypeModifier(ctx);
  if (itemModifierRow) reasoning_rows.push(itemModifierRow);



  // 6b. Consolidate hedge copy with profile-only warnings. When the
  // recommendation hedges ("Buy your usual size X") but the reasoning rows
  // surface a warning (e.g. height/length), append a short acknowledgment so
  // the headline doesn't read as ignoring the warning below it.
  if (
    recommendation.recommendation_type === "hedged" &&
    recommendation.recommendation_text.startsWith("Buy your usual size")
  ) {
    const warnRow = reasoning_rows.find((r) => r.icon === "warning");
    if (warnRow) {
      const ack = warningAcknowledgment(warnRow.text);
      recommendation.recommendation_text =
        `${recommendation.recommendation_text} — ${ack}`;
    }
  }

  // 7. Diagnostic copy
  const diagnostic_message = buildDiagnosticMessage(
    primary_gap,
    missing_fields,
    brand_fit,
    confidence_level,
  );

  const baseline: ConfidenceAssessment = {
    ...recommendation,
    confidence_level,
    confidence_score: Math.round(combined),
    garment_score,
    user_score,
    brand_fit_score,
    primary_gap,
    missing_fields,
    diagnostic_message,
    reasoning_rows,
    brand_fit_signal: brand_fit,
  };

  // Auth users only: augment with edge-fn signals (purchase outcomes,
  // size-chart-derived recommended_size). Guests pass no edge_tier_signal.
  const legacy = ctx.edge_tier_signal
    ? augmentWithEdgeSignals(baseline, ctx.edge_tier_signal, ctx)
    : baseline;

  // PR 3b — unified pipeline short-circuit runs AFTER brand_size_charts +
  // brand_fit_profiles lookups + edge merge so resolveSignals receives the
  // fully enriched ctx. Flag stays FALSE until PR 3c re-flips post-verify.
  if (UNIFIED_SCORING) {
    const { resolveSignals, computeFitScore, generateCardContent } =
      await import("./unifiedScoring");
    const signals = resolveSignals({
      ctx,
      retailer_canonical: retailerId,
      brand_size_chart_convention,
    });
    const score = computeFitScore(signals);
    return generateCardContent(score, signals);
  }

  // PQ-5c — mirror lens computation on the legacy path. ADD-only. Lens copy
  // for Suits-you comes from the sparkle row in reasoning_rows when present.
  const { buildLensSystem, retailerDisplayName } = await import("./unifiedScoring");
  const sparkleRow = legacy.reasoning_rows?.find((r) => r.icon === "sparkle");
  legacy.lenses = buildLensSystem({
    tiersFired: legacy.tiers_fired ?? [],
    recommendationText: legacy.recommendation_text ?? legacy.reasoning ?? null,
    stylingCopy: sparkleRow?.text ?? null,
    userSize: ctx.user_size ?? null,
    confidenceScore: legacy.confidence_score,
    brand: retailerId ? retailerDisplayName(retailerId) : null,
  });
  return legacy;
}



/**
 * Enriches the baseline `ConfidenceAssessment` with signals from the
 * `fit-confidence` edge fn. Strictly additive — never downgrades baseline
 * confidence and never overrides an already-confident absolute/adjustment
 * recommendation. Safe to call with partial/missing edge data.
 */
export function augmentWithEdgeSignals(
  baseline: ConfidenceAssessment,
  edge: EdgeTierSignal,
  ctx: Pick<ConfidenceContext, "user_size" | "retailer">,
): ConfidenceAssessment {
  const result: ConfidenceAssessment = {
    ...baseline,
    reasoning_rows: [...(baseline.reasoning_rows ?? [])],
  };

  const outcomeCount = edge.data_sources?.outcome_count ?? 0;
  const hasOutcomes = edge.data_sources?.has_outcomes ?? false;

  // Signal 1 — purchase outcomes upgrade.
  // The edge fn returns confidence_level="high" only when perfectOutcomes>=3
  // AND poorOutcomes<2, so we mirror that gate via the level itself.
  if (
    hasOutcomes &&
    outcomeCount >= 3 &&
    edge.confidence_level === "high" &&
    ctx.user_size
  ) {
    if (result.confidence_level !== "high") {
      result.confidence_level = "high";
      result.confidence_score = Math.max(result.confidence_score, 80);
    }
    if (result.recommendation_type === "hedged") {
      result.recommendation_type = "absolute";
      result.recommended_size = ctx.user_size;
      result.recommendation_text = `Buy size ${ctx.user_size} with confidence`;
      result.primary_gap = "none";
    }
    const outcomeRow = (edge.positive_rows ?? []).find((t) =>
      /fits you well|previous purchases?/i.test(t),
    );
    result.reasoning_rows.push({
      icon: "check",
      text:
        outcomeRow ??
        `Fits you well — ${outcomeCount} previous purchases from ${ctx.retailer ?? "this retailer"}`,
    });
  }

  // Signal 2 — recommended_size stepping (size-chart + brand-fit verdict).
  if (
    edge.recommended_size &&
    edge.recommended_size !== ctx.user_size &&
    result.recommendation_type === "hedged"
  ) {
    result.recommendation_type = "adjustment";
    result.recommended_size = edge.recommended_size;
    result.recommendation_text = `Buy size ${edge.recommended_size}`;
    result.primary_gap = "none";
    if (edge.recommended_size_note) {
      result.reasoning_rows.push({
        icon: "check",
        text: edge.recommended_size_note,
      });
    }
    if (result.confidence_level === "low") {
      result.confidence_level = "medium";
      result.confidence_score = Math.max(result.confidence_score, 55);
    }
  }

  // Signal 3 — surface server-emitted reasoning rows (warning_rows is the
  // brand-fit-tendency unlock, e.g. "Zara typically runs small"). De-dupe
  // against rows already on baseline to avoid repeating.
  const existingTexts = new Set(
    result.reasoning_rows.map((r) => r.text.trim().toLowerCase()),
  );
  const pushUnique = (icon: ReasoningRow["icon"], text: string) => {
    const key = text.trim().toLowerCase();
    if (!key || existingTexts.has(key)) return;
    existingTexts.add(key);
    result.reasoning_rows.push({ icon, text });
  };
  for (const t of edge.warning_rows ?? []) pushUnique("warning", t);
  for (const t of edge.positive_rows ?? []) {
    if (!/fits you well|previous purchases?/i.test(t)) pushUnique("check", t);
  }
  for (const t of edge.based_on ?? []) pushUnique("info", `Based on ${t}`);
  for (const t of edge.limited_by ?? []) pushUnique("info", `Limited by ${t}`);

  // Signal 4 — brand fit signal present + complete profile lifts medium → medium-high.
  const ds = edge.data_sources ?? {};
  if (
    edge.confidence_level === "medium" &&
    ds.has_brand_fit_signal &&
    ds.has_profile &&
    result.confidence_level === "medium"
  ) {
    result.confidence_level = "medium-high";
    result.confidence_score = Math.max(result.confidence_score, 65);
  }

  // Signal 5 — brand tendency nuance: when server emits a "runs small/large"
  // warning and we didn't step the size (no recommended_size), blend it into
  // the recommendation text so the headline is no longer generic. Only when
  // baseline was hedged (otherwise we'd clobber an authoritative absolute).
  const tendency = (edge.warning_rows ?? []).find((r) =>
    /runs (small|large)/i.test(r),
  );
  if (
    tendency &&
    ctx.user_size &&
    !edge.recommended_size &&
    result.recommendation_type === "hedged"
  ) {
    // Extract brand name from the warning row (e.g. "Zara typically runs
    // small — consider sizing up.") — falls back to ctx.retailer.
    const brandMatch = tendency.match(/^([A-Z][\w& '-]*?)\s+(?:typically|often|runs)/);
    const brandName = brandMatch?.[1] ?? ctx.retailer ?? "This brand";
    if (/runs small/i.test(tendency)) {
      const stepped = adjustSize(ctx.user_size, "up");
      result.recommendation_type = "adjustment";
      result.recommended_size = stepped;
      result.recommendation_text = `Consider sizing up to ${stepped} — ${brandName} typically runs small`;
      const pushUniqueAck = (icon: ReasoningRow["icon"], text: string) => {
        const key = text.trim().toLowerCase();
        if (existingTexts.has(key)) return;
        existingTexts.add(key);
        result.reasoning_rows.push({ icon, text });
      };
      pushUniqueAck("check", `Based on your usual ${ctx.user_size} and ${brandName}'s fit tendency`);
      pushUniqueAck("warning", "Brand runs small — sizing up gives a safer fit");
    } else if (/runs large/i.test(tendency)) {
      const stepped = adjustSize(ctx.user_size, "down");
      result.recommendation_type = "adjustment";
      result.recommended_size = stepped;
      result.recommendation_text = `Consider sizing down to ${stepped} — ${brandName} typically runs large`;
      const pushUniqueAck2 = (icon: ReasoningRow["icon"], text: string) => {
        const key = text.trim().toLowerCase();
        if (existingTexts.has(key)) return;
        existingTexts.add(key);
        result.reasoning_rows.push({ icon, text });
      };
      pushUniqueAck2("check", `Based on your usual ${ctx.user_size} and ${brandName}'s fit tendency`);
      pushUniqueAck2("warning", "Brand runs large — sizing down gives a closer fit");
    }
  }

  if (
    result.recommendation_type !== baseline.recommendation_type ||
    result.confidence_level !== baseline.confidence_level
  ) {
    console.log(
      `[recommendation-augment] baseline: ${baseline.recommendation_type}/${baseline.confidence_level} → ` +
        `augmented: ${result.recommendation_type}/${result.confidence_level} ` +
        `(outcome_count=${outcomeCount}, edge_level=${edge.confidence_level}, ` +
        `recommended_size=${edge.recommended_size ?? "—"})`,
    );
  }

  return result;
}

/**
 * Normalise free-form gender strings ("woman", "female", "F", "Women") onto
 * the canonical "women"/"men" axis used by brand lookup tables. Returns null
 * when input is non-binary, unknown, or empty so callers can fall back.
 */
export function normaliseGender(
  g: string | null | undefined,
): "women" | "men" | null {
  if (!g) return null;
  const lower = String(g).trim().toLowerCase();
  if (["woman", "women", "female", "f"].includes(lower)) return "women";
  if (["man", "men", "male", "m"].includes(lower)) return "men";
  return null;
}

// ---------------------------------------------------------------------------
// Test snippets (Cases A–F — comment-form expectations).
// ---------------------------------------------------------------------------
// Case A — Full data, direct Zara verdict:
//   ctx: full profile, rich garment, retailer "Zara",
//        user_brand_fit_tops contains { brand:"Zara", verdict:"true_to_size", category:"tops" }
//   → garment_score≈50, user_score=50, brand_fit_score=50 (direct)
//   → base=50, combined=50+25=75 → "medium-high" (just below "high"; if size_chart present full 50, still 75)
//   → recommendation absolute "Buy size {user_size}", reasoning from brand_fit
// Case B — Full user, only fit_type on garment, no brand fit:
//   garment_score=10, user_score=50, brand_fit_score=0
//   → sqrt(500)≈22 → "low"; primary_gap="garment_data"
//   → diagnostic: "limited data on this garment"
// Case C — Only user_size, rich garment:
//   garment_score=50, user_score=20, brand_fit_score=0
//   → sqrt(1000)≈32 → "medium"; primary_gap="user_data"
//   → diagnostic: "Add your height to improve confidence"
// Case D — Brand fit conflict (curated H&M=large, COS=small for Uniqlo):
//   brand_fit.conflict_detected=true, brand_fit_score=0
//   → primary_gap="brand_fit_conflict"
//   → diagnostic mentions mixed signals
// Case E — user_size null:
//   recommendation_type="prompt_user", missing_fields[0]={field:"size", priority:"high"}
//   → diagnostic: "Tell us your usual size to get a recommendation."
// Case F — Direct brand fit "runs_small" but fit_copy="fits true to size":
//   fit_copy locks size → adjustment skipped, recommended_size = user_size, type "absolute"
//   reasoning: "Fits true to size — your usual size works"
//   brand_fit_score still contributes to confidence (high)

